import React, { useEffect } from 'react';
import { CreditCardIcon } from '@heroicons/react/24/outline';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  CheckCircleIcon as CheckCircle, 
  CalendarIcon as Calendar, 
  ArrowRightIcon as ArrowRight,
  DocumentTextIcon as FileText,
  BellIcon as Bell,
  ShieldCheckIcon as Shield,
  ArrowDownTrayIcon as Download
} from '@heroicons/react/24/outline';
import confetti from 'canvas-confetti';
import { useToast } from '../../../contexts';

interface SubscriptionData {
  id: string;
  name: string;
  amount: number;
  billing_cycle: 'MONTHLY' | 'YEARLY';
  next_billing_date: string;
  status: string;
  billing_key: {
    card_company: string;
    card_number: string;
  };
}

const SubscriptionSuccess: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess } = useToast();
  const subscription = location.state?.subscription as SubscriptionData;

  useEffect(() => {
    if (!subscription) {
      navigate('/subscription/plans');
      return;
    }

    // 성공 효과
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 }
    });

    // 성공 메시지
    showSuccess('정기구독이 성공적으로 시작되었습니다!');
  }, [subscription, navigate, showSuccess]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('ko-KR').format(price);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ko-KR');
  };

  if (!subscription) {
    return null;
  }

  const benefits = [
    {
      icon: <Calendar className="w-5 h-5" />,
      title: '자동 갱신',
      description: '매월 자동으로 갱신되어 편리합니다'
    },
    {
      icon: <Shield className="w-5 h-5" />,
      title: '언제든 해지 가능',
      description: '위약금 없이 언제든지 해지할 수 있습니다'
    },
    {
      icon: <Bell className="w-5 h-5" />,
      title: '사전 알림',
      description: '결제 3일 전 미리 알려드립니다'
    },
    {
      icon: <FileText className="w-5 h-5" />,
      title: '결제 내역 제공',
      description: '매월 상세한 결제 내역을 확인할 수 있습니다'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-3xl mx-auto px-4">
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* 헤더 섹션 */}
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-8 text-white text-center">
            <CheckCircle className="w-16 h-16 mx-auto mb-4" />
            <h1 className="text-3xl font-bold mb-2">구독 신청 완료!</h1>
            <p className="text-blue-100">
              정기구독이 성공적으로 시작되었습니다
            </p>
          </div>

          {/* 구독 정보 */}
          <div className="p-8">
            <div className="bg-blue-50 rounded-lg p-6 mb-8">
              <h2 className="text-lg font-semibold mb-4">구독 정보</h2>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">구독 상품</span>
                  <span className="font-medium">{subscription.name}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">결제 금액</span>
                  <span className="font-medium text-lg">
                    ₩{formatPrice(subscription.amount)}
                    <span className="text-sm text-gray-600 ml-1">
                      /{subscription.billing_cycle === 'YEARLY' ? '년' : '월'}
                    </span>
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">다음 결제일</span>
                  <span className="font-medium">
                    {formatDate(subscription.next_billing_date)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">결제 수단</span>
                  <div className="flex items-center gap-2">
                    <CreditCardIcon className="w-4 h-4 text-gray-400" />
                    <span className="font-medium">
                      {subscription.billing_key.card_company} {subscription.billing_key.card_number}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* 구독 혜택 */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4">구독 혜택</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex gap-3 p-4 bg-gray-50 rounded-lg">
                    <div className="text-blue-600">{benefit.icon}</div>
                    <div>
                      <h4 className="font-medium text-gray-800">{benefit.title}</h4>
                      <p className="text-sm text-gray-600 mt-1">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 액션 버튼 */}
            <div className="space-y-3">
              <button
                onClick={() => navigate('/subscription/manage')}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              >
                구독 관리 페이지로 이동
                <ArrowRight className="w-5 h-5" />
              </button>
              <button
                onClick={() => window.print()}
                className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-lg font-medium hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
              >
                <Download className="w-5 h-5" />
                영수증 다운로드
              </button>
            </div>

            {/* 안내 사항 */}
            <div className="mt-8 p-6 bg-amber-50 rounded-lg">
              <h4 className="font-medium text-amber-800 mb-2">안내사항</h4>
              <ul className="text-sm text-amber-700 space-y-1">
                <li>• 첫 결제가 즉시 진행되었습니다</li>
                <li>• 다음 결제일 3일 전에 알림을 보내드립니다</li>
                <li>• 구독 관리 페이지에서 언제든지 해지할 수 있습니다</li>
                <li>• 결제 관련 문의는 support@attend.co.kr로 연락주세요</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionSuccess;